<template>
  <div class="visualizer-intro critical-component-clear container no-padding">
    <div
      class="visualizer-intro__media"
      :class="`visualizer-intro__media--${liquid.mediaType}`"
    >
      <ArtDirection
        v-if="liquid.image.src && liquid.mediaType === 'image'"
        :breakpoint-sizes="'640-1152-1536-2048-2460-2880'"
        :block-class="'visualizer-intro'"
        :image="liquid.image"
        :ratio-desktop="'4:3'"
        :ratio="'15:14'"
        :padding="true"
      />
      <responsive-video
        v-if="liquid.video && liquid.mediaType === 'video'"
        :breakpoint-sizes="'640-1152-1536-2048-2460-2880'"
        :block-class="'visualizer-intro'"
        :video-src="liquid.video"
        :ratio-desktop="'4:3'"
        :ratio="'15:14'"
        :padding="true"
      />
    </div>

    <div class="visualizer-intro__content">
      <badge
        v-if="liquid.badgeText"
        block-class="visualizer-intro"
        :label="liquid.badgeText"
        :modifiers="[liquid.badgeColor, badgeModifier]"
      />

      <div
        v-if="activePage === 'start'"
        class="visualizer-intro__intro"
      >
        <highlighter-title
          v-if="liquid.introTitle"
          block-class="visualizer-intro"
          :highlighter-color="liquid.introTitleHighlightColor"
          :highlighter-copy="liquid.introTitle"
          custom-element="h1"
        />

        <p
          v-if="liquid.introDescription"
          class="visualizer-intro__description"
          v-html="$cleanRTE(liquid.introDescription, { useTextLink: true })"
        />

        <div class="visualizer-intro__buttons">
          <btn
            v-for="cta in liquid.ctas"
            :key="cta.id"
            block-class="visualizer-intro"
            type="button"
            :data-intro-type="cta.type"
            :icon="false"
            :label="cta.label"
            :modifiers="[cta.style, 'block', 'center']"
            @click="handleIntroCtaClick"
          />
        </div>
      </div>

      <div
        v-if="activePage === 'bedding-size'"
        class="visualizer-intro__bedding-size"
      >
        <div class="visualizer-intro__controls body-2">
          <button
            class="visualizer-intro__button visualizer-intro__button-back"
            @click="handleBackClick"
            v-html="$icon('direction.arrow.left')"
          />
        </div>

        <highlighter-title
          v-if="liquid.beddingTitle"
          block-class="visualizer-intro"
          :highlighter-color="liquid.beddingTitleHighlightColor"
          :highlighter-copy="liquid.beddingTitle"
          custom-element="h1"
        />

        <p
          v-if="liquid.beddingDescription"
          class="visualizer-intro__description"
          v-html="$cleanRTE(liquid.beddingDescription, { useTextLink: true })"
        />

        <div class="visualizer-intro__buttons">
          <template v-for="size in beddingSizes">
            <btn
              v-if="size.availableInFlows.includes(activeFlow)"
              :key="size.label"
              block-class="visualizer-intro"
              type="button"
              :data-bedding-size="size.id"
              :icon="false"
              :label="size.label"
              :modifiers="[
                'secondary',
                'block',
                'center',
              ]"
              @click="handleBeddingCtaClick"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Visualizer intro (visualizer-intro)
 * -----------------------------------------------------------------------------
 * A full width banner used as an entrypoint into the bed visualizer app
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import ArtDirection from '~global/images/art-direction'
import Badge from '~global/badge/badge'
import Btn from '~global/btn/btn'
import HighlighterTitle from '~global/highlighter-title/highlighter-title'

import { convertToHandle } from '~/helpers/convert'

const bedVisualizerCustomizeSteps = require('../bed-visualizer/customize-steps')

export default {
  name: 'VisualizerIntro',

  components: {
    ArtDirection,
    Badge,
    Btn,
    HighlighterTitle,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        badgeColor: '',
        badgeText: '',
        beddingDescription: '',
        beddingTitle: '',
        beddingTitleHighlightColor: '',
        ctas: [],
        image: {},
        introDescription: '',
        introTitle: '',
        introTitleHighlightColor: '',
        sectionId: '',
      }),
    },
  },

  data() {
    return {
      activePage: 'start',
      beddingSizes: [
        {
          id: 'twinTwinxl',
          availableInFlows: ['collections', 'custom'],
          label: this.$string('bedVisualizer.sizes.twinTwinxl'),
          filters: [
            'twin',
            'twin xl',
            'twin/twin xl',
            'twin / twin xl',
            'twin/3"',
            'twin / 3"',
            'twin xl 3"',
            'twin xl/2"',
            'twin xl / 2"',
          ],
        },
        {
          id: 'full',
          availableInFlows: ['custom'],
          label: this.$string('bedVisualizer.sizes.full'),
          filters: [
            'full',
            'full/queen',
            'full / queen',
            'full/3"',
            'full / 3"',
            'full xl/3"',
            'full xl / 3"',
          ],
        },
        {
          id: 'queen',
          availableInFlows: ['custom'],
          label: this.$string('bedVisualizer.sizes.queen'),
          filters: [
            'queen',
            'full/queen',
            'full / queen',
            'queen/3"',
            'queen / 3"',
          ],
        },
      ],
      debugMode: false,
      gtmEvent: 'BedStylerFunnel',
      pages: [
        'start',
        'bedding-size',
      ],
      activeFlow: 'custom',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      config: (state) => state['bed-visualizer'].config,
    }),

    /**
     * Badge modifier.
     */
    badgeModifier() {
      if (this.activePage === 'bedding-size') {
        return this.activePage
      }

      return ''
    },
  },

  created() {
    this.checkRoute()
  },

  mounted() {
    this.setEventListeners()
    this.setInitialPage()

    if (this.debugMode) {
      this.openBedVisualizer()
    }
  },

  unmounted() {
    this.removeEventListeners()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      resetConfig: 'bed-visualizer/resetConfig',
      saveConfig: 'bed-visualizer/saveConfig',
      updateGlobalFilters: 'bed-visualizer/updateGlobalFilters',
      updateType: 'bed-visualizer/updateType',
    }),

    /**
     * Set initial page on load.
     */
    setInitialPage() {
      // Directly open the Bed Visualizer
      this.openBedVisualizer();
    },

    /**
     * Update history with current state.
     * @param {String} page -Force a page to push to history. Defaults to active
     *  page (optional)
     */
    pushToHistory(page = this.activePage) {
      const state = { page }
      const isBaseRoute = page === 'start'
      const url = `${this.$variable('routes.visualizer')}${isBaseRoute ? '' : `/${convertToHandle(page)}`}`

      window.history.pushState(state, '', url)
    },

    /**
     * Replace history with current state.
     */
    replaceHistory() {
      const state = { page: this.activePage }
      window.history.replaceState(state, '')
    },

    /**
     * Change page.
     * @param {String} page - Page name.
     * @param {Boolean} initialLoad - Is initial page load.
     * @param {Boolean} fromHistory - Change came from browser forward/back btn
     */
    changePage(page, initialLoad = false, fromHistory = false) {
      this.activePage = page

      if (fromHistory) {
        return
      }

      if (initialLoad) {
        this.replaceHistory()
      } else {
        this.pushToHistory()
      }
    },

    /**
     * Get page from URL.
     */
    getPageFromUrl() {
      const baseRoute = this.$variable('routes.visualizer')
      const regex = new RegExp(`${baseRoute}\/([\\w-]+)`, 'i')
      const match = window.location.pathname.match(regex)

      return match && match[1]
    },

    /**
     * Check route. If the route is further in the flow than the visualizer-intro
     * (e.g. /pages/visualizer/bedding) reset back to the first page of
     * visualizer-intro
     */
    checkRoute() {
      const page = this.getPageFromUrl()

      if (!page) {
        return
      }

      const isVisualizerIntroRoute = this.pages.some((route) => {
        return page === convertToHandle(route)
      })

      /* 
      * If route is /headboard , load /bedding-size but also open the bedVisualizer
      */
      if(page === 'headboard'){
        return;
      }

      if (!isVisualizerIntroRoute) {
        const baseRoute = this.$variable('routes.visualizer')
        const state = { page: this.pages[0] }
        window.history.replaceState(state, '', baseRoute)
      }
    },

    /**
     * Handle back click.
     */
    handleBackClick() {
      this.goToPrevPage()
    },

    /**
     * Intro CTA click handler.
     */
    handleIntroCtaClick(e) {
      // Go straight to Bed Visualizer
      window.location = 'https://www.dormify.com/pages/visualizer/headboard';
    },

    /**
     * Bedding CTA click handler.
     */
    handleBeddingCtaClick(e) {
      const element = e.target.closest('button')
      const activeFlowChanged = this.config.activeFlow !== this.activeFlow
      const beddingSize = this.beddingSizes.find(data =>
        data.id === element.dataset.beddingSize,
      )
      const sizeChanged = this.config?.filters?.size?.join('') !== beddingSize.filters.join('')

      /**
       * If flow type or size is changed, reset the entire config so user starts
       * from the beginning.
       */
      if (activeFlowChanged  || sizeChanged) {
        this.resetConfig()
      }

      // Update bed visualizer config then save it
      this.updateType(this.activeFlow)
      this.updateGlobalFilters({ size: beddingSize.filters })
      this.saveConfig()

      this.openBedVisualizer()

      const gtmLabel = [
        'twin', 'full', 'queen',
      ].filter(label =>
        beddingSize.label.toLowerCase().indexOf(label) > -1,
      )
      this.$gtmTrack(
        'BedStylerFunnel',
        `size_selection_${gtmLabel}`,
      )
    },

    /**
     * Open bed visualizer.
     */
    openBedVisualizer(fromHistory = false) {
      this.openOverlay({
        component: 'bed-visualizer',
        ignoreDismissed: true,
        namespace: 'bedVisualizer',
      })

      if (fromHistory) {
        return
      }

      /**
       * We want to make a history entry when bed-visualizer is opened from a
       * user action but not from clicking the browser forward/back buttons.
       *
       * This is a workaround as props can't be passed to components that are
       * loaded async, so bed-visualizer doesn't know what caused it to open.
       *
       * The history value needs to be hardcoded to match the first item
       * of the pagesOrder array inside the bed-visualizer component.
       *
       */
      if (this.config.activeFlow === 'collections') {
        this.pushToHistory('Collections')

      } else {
        this.pushToHistory(bedVisualizerCustomizeSteps[0].name)
      }
    },

    /**
     * Handle history change.
     */
    handleHistoryChange() {
      const historyPage = window.history?.state?.page

      /**
       * Do nothing if no page state exists or page remains the same.
       */
      if (!historyPage || historyPage === this.activePage) {
        return
      }

      /**
       * If page is part of visualizer-intro, change to that page
       */
      if (this.pages.includes(historyPage)) {
        this.changePage(historyPage, false, true)
      }

      /**
       * Do not proceed if visualizer is already open as visualizer component
       * will handle it's own history changes. It has already pushed the
       * page change to the window history.
       */
      if (this.getActiveOverlay === 'bedVisualizer') {
        return
      }

      /**
       * If page exists but it is not part of the visualizer-intro component,
       * it must be part of the bed-visualizer so load the component.
       */
      if (!this.pages.includes(historyPage)) {
        this.openBedVisualizer(true)
        return
      }
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      window.addEventListener('popstate', this.handleHistoryChange)
    },

    /**
     * Remove event listeners.
     */
    removeEventListeners() {
      window.removeEventListener('popstate', this.handleHistoryChange)
    },

    /**
     * Get next page.
     */
    getNextPage() {
      const nextIndex = this.pages.indexOf(this.activePage) + 1
      return this.pages[nextIndex]
    },

    /**
     * Get prev page.
     */
    getPrevPage() {
      const prevIndex = this.pages.indexOf(this.activePage) - 1
      return this.pages[prevIndex]
    },

    /**
     * Go to next page.
     */
    goToNextPage() {
      this.changePage(this.getNextPage())
    },

    /**
     * Go to prev page.
     */
    goToPrevPage() {
      this.changePage(this.getPrevPage())
    },
  },
}
</script>

<style lang="scss">
@import './visualizer-intro';
</style>
